<template>
    <div 
        class="page-details"
        @focusin="onFocusDetailSeries"
        @focusout="onFocusoutDetailSeries"
    >
        <details-header
            ref="showInfo"
            :item="currentItem"
            :seasons="seasons"
            :is-playable="false"
            design="series"
            @unset-focused="focusFirstRow"
            @show-trailer="showTrailer"
        />
        <div>
            <Lines-detail-show 
                ref="linesDetailShow" 
                @ready="readyLines"
                @list-item-selected="itemsSelected"
            />
        </div>
    </div>
</template>

<script setup lang="ts">
    import LinesDetailShow from "@/components/widgets/LinesDetailShow/LinesDetailShow.vue";
    import {PlayItem} from "@ca.vmedia.tv/domains";
    import {AppConstants} from "@/common";
    import type {DetailsHeaderRef} from "@/components/widgets/DetailsHeader/types";
    import type { EpisodeCardData, RelatedOnDemandCardData, CreditCardData } from "@/components/Cards/types";
    import { linkItems } from "@/components/Header/types/LinkItemsCollection";
    import { useAccountStore } from "@/stores/Account";
    import { useApplicationStore } from "@/stores/Application";
    import { Logger } from '@/utils';
    import type { LinesDetailShowRef } from "@/components/widgets/LinesDetailShow/types";
    import { computed, ref, type Ref, onMounted, watch, nextTick, onBeforeUnmount } from "vue";
    import { useRouter } from 'vue-router';
    import { storeToRefs } from 'pinia'
    import { useUserInfoStore } from "@/stores/UserInfo";
    import { useParentControlStore } from "@/stores/ParentControl";
    import { useFocus } from "@/composables/useFocus";
    import { useDetailShowPageStore } from "@/stores/DetailShowPage";
    import { usePlayerStore } from "@/stores/Player";
    import type { IEventSelected } from "@/components/MultiLineView/types";
    import { useDetailPageStore } from "@/stores/DetailPage";
    import { Strings } from "@/locales/types/enums/strings";
    import { ScreenName } from "@/common/types/screen-names";
    import { Param as AnalyticsParam } from "@/plugins/analytics/interfaces/analytics.service.interface";
    import { useGenresStore } from "@/stores/Genres";
    import { useCastPageStore } from "@/stores/CastPage";
   
    const {
        setViewedCastDetailItems
    } = useCastPageStore();
    
    const DetailPageStoreModule = useDetailPageStore();
    const {
        setViewedDetailItems
    } = DetailPageStoreModule;
    const {
        getCurrentItemDetail: currentItem,
    } = storeToRefs(DetailPageStoreModule);

    const ApplicationStore = useApplicationStore();
    const {
        getIsPreviewSingInModal: isPreviewSignInModal,
        getIsHaveFocusedElement: isHavSelectedIdProgram,
    } = storeToRefs(ApplicationStore);
    const {
        showPreviewSignInModal, 
        setTooltipeShowing,
        setTooltipeMessage
    } = ApplicationStore;

    const PlayerStoreModule = usePlayerStore();
    const {
        startPlayingProgram,
    } = PlayerStoreModule;

    const GenresStoreModule = useGenresStore();
    const {
        getTopGenres,
    } = storeToRefs(GenresStoreModule);

    definePageMeta({ 
        layout: 'default-no-header',
        middleware: ['set-page-indicator', 'fetch-detail-show'],
    });

    const { setIsHaveFocusedElement, focusElementLine } = useFocus();

    const DetailShowPage = useDetailShowPageStore();
    const {
        getSeasons: seasons
    } = storeToRefs(DetailShowPage);

    const AccountStoreModule = useAccountStore();
    const {
        getForgotModalVisible: isForgotModalVisible
    } = storeToRefs(AccountStoreModule);
    const ParentControlStoreModule = useParentControlStore();
    const {
        getIsModalParentVisible: isTypeModeEnabledPin
    } = storeToRefs(ParentControlStoreModule);

    const UserInfoStoreModule = useUserInfoStore();
    const {
        isGuest,
    } = storeToRefs(UserInfoStoreModule);


    const { t } = useI18n();
    const { $fire, $eventBus, $navigator, $analytics } = useNuxtApp();
    const router = useRouter();

    const traceDetailsScreen = $fire.trace($fire.performance, 'DETAILS_SCREEN');
    try {
        traceDetailsScreen.start();
        Logger.captureLog('DETAILS_SCREEN start');
    } catch (e){
        Logger.captureLog(e);
    }

    const showInfo: Ref<DetailsHeaderRef | null> = ref(null);
    const isReadyLines: Ref<boolean> = ref(false);
    const linesDetailShow: Ref<LinesDetailShowRef | null> = ref(null);
    const isRegKeyListenersDetailSeries: Ref<boolean> = ref(false);

    const loadedComponents = computed((): boolean => linesDetailShow.value !== null && showInfo.value !== null);
    const isReadyAll = computed((): boolean => isReadyLines.value && loadedComponents.value);
    const allowRegistrationKeyListeners = computed((): boolean => !isTypeModeEnabledPin.value && !isForgotModalVisible.value);

    const focusToDetailHeader = () => {
        if (showInfo.value) {
            showInfo.value.focus();
        }
    };
    const readyLines = () => {
        isReadyLines.value = true;
    };
    const showTrailer = (trailerVideoId: string): void => {
        if (trailerVideoId) {
            void router.push(`${linkItems.trailer.routerPath}?youtubeVideoId=${trailerVideoId}`);
        }
    };
    const onFocusDetailSeries = () => {
        registerKeyListenersDetailSeries();
    };
    const onFocusoutDetailSeries = () => {
        unregisterKeyListenersDetailSeries();
    };
    const registerKeyListenersDetailSeries = () => {
        if (!isRegKeyListenersDetailSeries.value) {
            $navigator.on("keypress:back", keyBackListenerDetailSeries);
            isRegKeyListenersDetailSeries.value = true;
        }
    };
    const unregisterKeyListenersDetailSeries = () => {
        if (isRegKeyListenersDetailSeries.value) {
            $navigator.off("keypress:back", keyBackListenerDetailSeries);
            isRegKeyListenersDetailSeries.value = false;
        }
    };
    const keyBackListenerDetailSeries = () => {
        router.go(-1);
    };
    const itemsSelected = (val: IEventSelected) => {
        switch (val.lineId) {
            case AppConstants.shelfKeys.episodesCards:
                onEpisodeSelected(val.item as EpisodeCardData);
                break;

            case AppConstants.shelfKeys.relatedCards:
                onRelatedSelectedDetailSeries(val.item as RelatedOnDemandCardData);
                break;

            case AppConstants.shelfKeys.castCards:
                onCastSelectedDetailSeries(val.item as CreditCardData);
                break; 
        }
    };
    const onCastSelectedDetailSeries = (credit: CreditCardData) => {
        try {
            const creditItem = credit.getCreditItem();
            if (!creditItem) {
                throw new Error("СreditItem is null:" + JSON.stringify(credit));
            }

            const personId = credit.getPersonId();
            if (!personId) {
                throw new Error("The person ID is null:" + JSON.stringify(credit));
            }

            setViewedCastDetailItems(creditItem);

            void router.push(`${linkItems.detailCast.routerPath}/${personId}`);
        } catch (error) {
            Logger.captureError(error as Error);
            setTooltipeMessage(t(Strings.DETAILS_TOOLTIP_ERROR_OPENING_DETAILS_PAGE).toString());
            setTooltipeShowing({visible: true});
        }
    };
    const focusFirstRow = () => {
        if (linesDetailShow.value) {
            linesDetailShow.value.focusFirstRow();
        }
    };
    const focusElement = () => { 
        if (isHavSelectedIdProgram.value) {
            focusElementLine();
        } else {
           focusToDetailHeader();
        }
    };
    const onRelatedSelectedDetailSeries = (onDemandItem: RelatedOnDemandCardData) => {
        try {
            const playItem = onDemandItem.getPlayItem();
            if (!playItem) {
                throw new Error("playItem is null:" + JSON.stringify(onDemandItem));
            }
            setViewedDetailItems(playItem);

            const [ id ] = onDemandItem.getId().split('_');

            const path = PlayItem.isSeries(playItem) ?
                `${linkItems.detailsVodSeries.routerPath}/${id}` :
                `${linkItems.detailsVodMovies.routerPath}/${id}`;

            void router.push(path);
        } catch (error) {
            Logger.captureError(error as Error);
            setTooltipeMessage(t(Strings.DETAILS_TOOLTIP_ERROR_OPENING_DETAILS_PAGE).toString());
            setTooltipeShowing({visible: true});
        }
    };
    const onEpisodeSelected = (episodeItem: EpisodeCardData) => {
        if (!isGuest.value) {
            startPlayingProgram(episodeItem.getPlayItem());
            void router.push(linkItems.player.routerPath);
        } else {
            setTimeout(() => {
                showPreviewSignInModal(true);
            }, 150);
        }
    };

    const initFocus = (): void => {
        setIsHaveFocusedElement();
        focusElement();
    };
    
    const handlerReady = (): void => {
       void nextTick(() => {
            initFocus();
            if (traceDetailsScreen) {
                try {
                    traceDetailsScreen.stop();
                    Logger.captureLog(`DETAILS_SCREEN stop ${traceDetailsScreen.durationUs / 1000000} sec`);
                } catch (e){
                    Logger.captureLog(e);
                }
            }
        });
    };

    watch(
        allowRegistrationKeyListeners,
        (value: boolean) => {
            if (value) {
                initFocus();
            } else {
                unregisterKeyListenersDetailSeries();
            }
        }
    );
    watch(
        isPreviewSignInModal,
        (value: boolean) => {
            if (value) {
                unregisterKeyListenersDetailSeries();
            } else {
                initFocus();
            }
        }
    )

    watch(
        isReadyAll,
        (value: boolean) => {
            if (value) {
                handlerReady();
            }
        }
    );

    onMounted(() => {
       void nextTick(() => {
            $eventBus.on("key-up-rows", focusToDetailHeader);
            if (currentItem.value) {
                const currentGenre = getTopGenres.value?.find((item) => item.id === currentItem.value?.topGenreId);

                $analytics.createCustomScreenEvent({
                    [AnalyticsParam.FA_SCREEN_NAME]: ScreenName.VOD_SERIES_DETAILS,
                    [AnalyticsParam.FA_SCREEN_CLASS]: ScreenName.VOD_SERIES_DETAILS,
                    [AnalyticsParam.ITEM_ID]: currentItem.value.id ? String(currentItem.value.id) : null,
                    [AnalyticsParam.CONTENT_TYPE]: currentItem.value.contentType,
                    [AnalyticsParam.CHANNEL_ID]: currentItem.value.topGenreId,
                    [AnalyticsParam.CHANNEL_NAME]: currentGenre?.name,
                    [AnalyticsParam.SERIES_NAME]: currentItem.value?.seriesName,
                });
            }
        });
    });

    onBeforeUnmount(() => {
        $eventBus.off("key-up-rows",focusToDetailHeader);
        unregisterKeyListenersDetailSeries();
    });
</script>

<style lang="scss">
    @import url("@/assets/styles/pages/_details.scss");
</style>